import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useMutation, useQueries } from 'react-query';
import { Snackbar } from '@mui/material';
import { Main as Container } from 'Components/Main';
import { PageHeader, Select, TextField } from 'Components/ui';
import { useAuthTenant } from 'store/auth';
import { getGapAnalysis, postGapAnalysisReport } from '../../../../services/gap-analysis';

const FormCard = styled.div`
  max-width: 520px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const CardTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`;

const CardSubtitle = styled.p`
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 24px;
  line-height: 1.55;
`;

const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const InfoBanner = styled.div`
  background: var(--vo-mist);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.p`
  font-size: 12px;
  color: var(--vo-celeste);
  margin: 0;
  line-height: 1.5;
`;

const BtnsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
`;

const BtnCancel = styled.button`
  height: 48px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s;
  &:hover { background: var(--vo-mist); }
`;

const BtnSubmit = styled.button`
  height: 48px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: .65; cursor: not-allowed; }
`;

export function ReportsGapAnalysisForm() {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [name, setName] = useState('');
  const [gapId, setGapId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const results = useQueries([
    {
      queryKey: ['gapAnalysis', selectedTenant.slug],
      queryFn: () => getGapAnalysis(selectedTenant.slug),
      enabled: !!selectedTenant.slug,
    },
  ]);

  const gapOptions = results[0]?.data?.data ?? [];
  const loadingGapAnalysis = results[0]?.isLoading;

  const mutation = useMutation({
    mutationFn: postGapAnalysisReport,
    onSuccess: data => {
      setSnackbar({ open: true, message: t('dashboard:gapAnalysis.form.successCreate') });
      setTimeout(() => {
        navigate(`/reports/gap-analysis/show/${data.data._id}`);
      }, 1000);
    },
    onError: () => {
      setSnackbar({ open: true, message: t('dashboard:gapAnalysis.form.errorCreate') });
    },
  });

  const handleSubmit = () => {
    if (!name || !gapId) {
      setSnackbar({ open: true, message: t('dashboard:gapAnalysis.form.requiredFields') });
      return;
    }
    mutation.mutate({ name, gap_analysis_id: gapId, company_id: selectedTenant.slug });
  };

  return (
    <Container>
      <PageHeader
        title={t('dashboard:gapAnalysis.form.title')}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('dashboard:gapAnalysis.breadcrumbLabel'), route: '/reports/gap-analysis' },
          { label: t('dashboard:gapAnalysis.form.createTitle') },
        ]}
      />

      <FormCard>
        <CardTitle>{t('dashboard:gapAnalysis.form.createTitle')}</CardTitle>
        <CardSubtitle>{t('dashboard:gapAnalysis.form.subtitle')}</CardSubtitle>

        <FieldsWrap>
          <FieldGroup>
            <Label htmlFor="ga-report-name">{t('dashboard:gapAnalysis.form.nameLabel')}</Label>
            <TextField
              id="ga-report-name"
              type="text"
              fullWidth
              placeholder={t('dashboard:gapAnalysis.form.namePlaceholder')}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="ga-select">{t('dashboard:gapAnalysis.form.gapAnalysisLabel')}</Label>
            <Select
              name="gapId"
              value={gapId}
              disabled={loadingGapAnalysis}
              onChange={setGapId}
              options={[
                { value: '', label: loadingGapAnalysis ? '...' : t('dashboard:gapAnalysis.form.selectPlaceholder'), disabled: true },
                ...gapOptions.map(gap => ({ value: gap._id, label: gap.name })),
              ]}
            />
          </FieldGroup>

          <InfoBanner>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <InfoText>{t('dashboard:gapAnalysis.form.infoBanner')}</InfoText>
          </InfoBanner>

          <BtnsRow>
            <BtnCancel type="button" onClick={() => navigate('/reports/gap-analysis')}>
              {t('common:buttons.cancel')}
            </BtnCancel>
            <BtnSubmit type="button" onClick={handleSubmit} disabled={mutation.isLoading}>
              {mutation.isLoading
                ? t('dashboard:gapAnalysis.form.saving')
                : t('dashboard:gapAnalysis.form.generateReport')}
            </BtnSubmit>
          </BtnsRow>
        </FieldsWrap>
      </FormCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Container>
  );
}
